import React from 'react';
import { Button as MuiButton } from '@mui/material';
import {
    Block,
    FlexDiv,
    Heading2,
    Heading3,
    TextTiny,
    TextWithTooltipIcon,
    palette,
} from '@awarego-ui/components';
import { observer } from 'mobx-react';
import { useStores } from '../../../../hooks/use-stores';
import { useAssessmentType } from '../../useAssessmentType';
import SubHeaderSeparator from '../../../../components/header/SubHeaderSeparator';
import PreviewSyncLists from '../../../automations/SingleAutomation/PreviewSyncLists';

function EmployeeListSync({ editingAssessment, companyId, assessmentId }) {
    const { assessmentStore } = useAssessmentType();
    const { commonStore, store } = useStores();

    const handleManualSyncLists = async () => {
        assessmentStore.previewUsersToRemoveSelection.clearSelection();
        assessmentStore.previewUsersToAddSelection.clearSelection();
        await assessmentStore.loadPreviewSyncLists(companyId, assessmentId);
        commonStore.showConfirm(
            <FlexDiv pt={16}>
                <PreviewSyncLists
                    automationId={assessmentId}
                    companyId={companyId}
                    selectable={true}
                    syncStore={assessmentStore}
                />
            </FlexDiv>,
            'Sync',
            'Sync users',
            async () => {
                const result = await assessmentStore.syncUsers(
                    companyId,
                    assessmentId
                );
                if (result) {
                    commonStore.success('Users synced');
                }
            }
        );
    };

    const handleSyncAllLists = async () => {
        assessmentStore.previewUsersToAddSelection.setAllPagesSelected();
        assessmentStore.previewUsersToRemoveSelection.setAllPagesSelected();
        await assessmentStore.syncUsers(companyId, assessmentId);
        await assessmentStore.loadPreviewSyncLists(companyId, assessmentId);
    };

    const handleSwitchSyncLists = async () => {
        const action = editingAssessment.disableSyncLists ? 'Enable' : 'Disable';
        await assessmentStore.loadPreviewSyncLists(companyId, assessmentId);

        commonStore.showConfirm(
            <>
                {editingAssessment.disableSyncLists
                    ? 'Would you like to activate automatic synchronization of the user list? Enabling this feature ensures that any modifications to the user lists are immediately updated in this assessment.'
                    : 'Would you like to disable automatic synchronization of the user list? If you disable this feature, changes to the user lists will not be automatically updated in this assessment. However, you can still synchronize the lists manually.'}
                {editingAssessment.disableSyncLists && (
                    <FlexDiv pt={16}>
                        <PreviewSyncLists
                            automationId={assessmentId}
                            companyId={companyId}
                            selectable={true}
                            syncStore={assessmentStore}
                        />
                    </FlexDiv>
                )}
                {!editingAssessment.disableSyncLists &&
                    assessmentStore.previewSyncLists.some(
                        (x) => x.added || x.removed
                    ) && (
                        <FlexDiv column pt={16} gap={16}>
                            <FlexDiv column gap={8}>
                                <Heading3>Sync Required</Heading3>
                                <TextTiny>
                                    New Users:{' '}
                                    {
                                        assessmentStore.previewSyncLists.filter(
                                            (x) => x.added
                                        ).length
                                    }
                                </TextTiny>
                                <TextTiny>
                                    Obsolete Users:{' '}
                                    {
                                        assessmentStore.previewSyncLists.filter(
                                            (x) => x.removed
                                        ).length
                                    }
                                </TextTiny>
                            </FlexDiv>
                            <FlexDiv justifyCenter>
                                <MuiButton
                                    variant="contained"
                                    color="primary"
                                    onClick={async () => {
                                        await handleSyncAllLists();
                                        commonStore.cancelConfirm();
                                    }}
                                >
                                    Sync Now
                                </MuiButton>
                            </FlexDiv>
                        </FlexDiv>
                    )}
            </>,
            action,
            `${action} user list sync`,
            async () => {
                const result = await assessmentStore.switchSyncLists(
                    companyId,
                    assessmentId,
                    !editingAssessment.disableSyncLists
                );
                if (result) {
                    commonStore.success('User list sync switched');
                }
            }
        );
    };

    if (editingAssessment.isScorm) return null;

    return (
        <Block gap={16}>
            <TextWithTooltipIcon
                textComponent={<Heading3>Employee list sync</Heading3>}
                tooltipText={
                    editingAssessment.disableSyncLists
                        ? 'User sync is inactive. Changes to the employee lists will not be automatically updated in this assessment.'
                        : 'User sync is active. Users added to user lists in this assessment are automatically added to this assessment.'
                }
            />
            <FlexDiv gap={8} alignCenter>
                <Heading2
                    style={{
                        borderBottom: store.isAdmin
                            ? `1px dotted ${
                                  editingAssessment.disableSyncLists
                                      ? palette.vibrant.orange
                                      : palette.evergreen.aspargus
                              }`
                            : undefined,
                    }}
                    link={store.isAdmin || undefined}
                    onClick={store.isAdmin ? handleSwitchSyncLists : undefined}
                    color={
                        editingAssessment.disableSyncLists
                            ? palette.vibrant.orange
                            : palette.evergreen.aspargus
                    }
                >
                    {editingAssessment.disableSyncLists ? 'Inactive' : 'Active'}
                </Heading2>
                {editingAssessment.disableSyncLists && store.isAdmin && (
                    <>
                        <SubHeaderSeparator />
                        <TextTiny link onClick={handleManualSyncLists}>
                            Sync users
                        </TextTiny>
                    </>
                )}
            </FlexDiv>
        </Block>
    );
}

export default observer(EmployeeListSync);
